import { LockIcon } from "@/Icons/v2/BuildingSelectorIcons/Lock";
import { PencilIcon } from "@/Icons/v2/BuildingSelectorIcons/Pencil";
import { TrashIcon } from "@/Icons/v2/BuildingSelectorIcons/Trash";
import { trpc } from "@/Utils/trpc";

import { useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import type { SavedBuildingView } from "../types";

type Props = {
  view: SavedBuildingView;
  currentViewId?: string;
  editingViewId: string | null;
  setEditingViewId: (value: string | null) => void;
  applyView: (view: SavedBuildingView) => void;
};

export const SavedViewElement: React.FC<Props> = ({
  view,
  currentViewId,
  editingViewId,
  setEditingViewId,
  applyView,
}) => {
  const trpcUtils = trpc.useUtils();
  const [editName, setEditName] = useState("");

  const { mutate: editBuildingSelection } =
    trpc.config.updateBuildingSelection.useMutation({
      onSuccess: () => trpcUtils.config.getBuildingSelections.invalidate(),
    });

  const { mutate: deleteBuildingSelection } =
    trpc.config.deleteBuildingSelection.useMutation({
      onSuccess: () => trpcUtils.config.getBuildingSelections.invalidate(),
    });

  const active = useMemo(
    () => currentViewId === view.id,
    [currentViewId, view],
  );
  const editing = useMemo(
    () => editingViewId === view.id,
    [editingViewId, view],
  );

  const commitRename = useCallback(() => {
    const name = editName.trim();
    const id = editingViewId;

    setEditingViewId(null);

    if (!name || !id) return;

    editBuildingSelection({ buildingSelectionId: id.toString(), name });
  }, [editBuildingSelection, editName, editingViewId, setEditingViewId]);

  const startRename = useCallback(
    (view: SavedBuildingView) => {
      setEditingViewId(view.id);
      setEditName(view.name);
    },
    [setEditingViewId],
  );

  const deleteView = useCallback(
    (id: string) => {
      deleteBuildingSelection({ buildingSelectionId: id.toString() });
    },
    [deleteBuildingSelection],
  );

  return (
    <div
      onClick={editing ? undefined : () => applyView(view)}
      className={twMerge(
        "flex min-h-[34px] cursor-pointer items-center gap-2 rounded-[7px] py-[6px] pr-[9px] pl-[11px]",
        editing && "cursor-default",
        active ? "bg-lime-light" : "hover:bg-grey1",
      )}
    >
      {editing ? (
        <input
          autoFocus
          value={editName}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") startRename(view);
          }}
          onBlur={commitRename}
          className="min-w-0 flex-1 rounded-md border border-black px-[6px] py-[2px] text-[14px] font-semibold outline-none"
        />
      ) : (
        <span
          className={twMerge(
            "flex-1 truncate text-[13px]",
            active ? "font-semibold" : "font-medium",
          )}
        >
          {view.name}
        </span>
      )}
      {!editing && (
        <span className="text-grey6 flex-none text-[11px] font-semibold">
          {view.terminalIds ? view.terminalIds.length : "—"}
        </span>
      )}
      {view.locked ? (
        <span
          title="Built-in view"
          className="flex w-[24px] flex-none justify-center"
        >
          <LockIcon />
        </span>
      ) : (
        !editing && (
          <span className="flex flex-none gap-[2px]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                startRename(view);
              }}
              title="Rename"
              className="text-grey6 hover:bg-grey2 flex h-[24px] w-[24px] cursor-pointer items-center justify-center rounded hover:text-black"
            >
              <PencilIcon />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteView(view.id);
              }}
              title="Delete"
              className="text-grey6 hover:bg-grey2 flex h-[24px] w-[24px] cursor-pointer items-center justify-center rounded hover:text-black"
            >
              <TrashIcon />
            </button>
          </span>
        )
      )}
    </div>
  );
};
